"use client";

import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  fetchTicketRequest,
  clearCurrentTicket,
} from "@/store/slices/ticket.slice";
import { NavigationHeader } from "@/components/common/NavigationHeader";
import { Button } from "@/components/ui/button";
import { Edit, MoreHorizontal } from "lucide-react";
import { TicketCommunication } from "@/components/tickets/TicketCommunication";
import { TicketPropertiesPanel } from "@/components/tickets/TicketPropertiesPanel";

const TicketDetail = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { storeType, storeId, ticketId } = useParams<{
    storeType: string;
    storeId: string;
    ticketId: string;
  }>();

  const { currentTicket, ticketLoading, ticketError } = useAppSelector(
    (state) => state.tickets,
  );

  useEffect(() => {
    if (ticketId) {
      dispatch(fetchTicketRequest({ ticketId }));
    }

    return () => {
      dispatch(clearCurrentTicket());
    };
  }, [ticketId, dispatch]);

  const navigationItems = [
    {
      label: "Tickets",
      href: `/${storeType}/${storeId}/tickets`,
    },
    {
      label:
        currentTicket?.subject || currentTicket?.title || `Ticket #${ticketId?.slice(-8)}`,
      isActive: true,
    },
  ];

  if (ticketLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-96">
          <div className="text-muted-foreground">Loading ticket...</div>
        </div>
      </div>
    );
  }

  if (ticketError || !currentTicket) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="text-muted-foreground mb-4">
              {ticketError?.message || "Ticket not found"}
            </div>
            <Button
              onClick={() => navigate(`/${storeType}/${storeId}/tickets`)}
            >
              Back to Tickets
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 h-screen flex flex-col">
      {/* Navigation Header */}
      <NavigationHeader items={navigationItems} />

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">
            Ticket #{currentTicket.id.slice(-8)}
          </h1>
          <p className="text-muted-foreground">
            Created {new Date(currentTicket.created_at).toLocaleDateString()}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <Edit className="h-4 w-4" />
            Edit
          </Button>
          <Button variant="outline" size="sm">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex gap-6 flex-1 min-h-0">
        {/* Communication View */}
        <div className="flex-1 min-h-0">
          <TicketCommunication ticket={currentTicket} />
        </div>

        {/* Properties Panel */}
        <div className="flex-shrink-0">
          <TicketPropertiesPanel ticket={currentTicket} />
        </div>
      </div>
    </div>
  );
};

export default TicketDetail;
